<template>
  <mfc-input-box ref="editor" class="html-editor" :class="{'html-editor-focusing': focus}" :errors="cmpErrors">
    <label class="label-html-editor" :class="{'label-html-editor-focused': labelFocus}">{{label}}</label>
    <div class="" ref="editorField" :id="editorId" style="height: 400px;"></div>
    <textarea class="hidden-textarea" :name="name" v-model="content"></textarea>
  </mfc-input-box>
</template>

<script>
import MfcInputBox from './MfcInputBox'
import inputMixin from "../../mixins/inputMixin"
import Quill from "quill";
import 'quill/dist/quill.snow.css';
import QuillResizeImage from 'quill-resize-image';
Quill.register("modules/resize", QuillResizeImage);

/*
  При отображении HTML из MfcHtmlEditor
  добавляйте класс "ql-editor" к контейнеру.
  При необходимости сбрасывайте height и padding.
*/
export default {
  name: "MfcHtmlEditor",
  mixins: [inputMixin],
  components: {
    MfcInputBox,
  },
  props: {
    initOptions: {
      type: [Object],
    }
  },
  data() {
    return {
      options: this.initOptions,
      editorId: `quill-editor-${crypto.randomUUID()}`,
      quill: null,
      undoBtn: null,
      redoBtn: null,
      content: null,
      focus: false,
      labelFocus: false,
      tooltips: {
          'ql-bold': 'Жирный',
          'ql-italic': 'Курсив',
          'ql-underline': 'Подчеркнутый',
          'ql-strike': 'Зачеркнутый',
          'ql-list[value="ordered"]': 'Нумерованный список',
          'ql-list[value="bullet"]': 'Маркированный список',
          'ql-align[value=""]': 'Выравнивание по левому краю',
          'ql-align[value="center"]': 'Выравнивание по центру',
          'ql-align[value="right"]': 'Выравнивание по правому краю',
          'ql-align[value="justify"]': 'Выравнивание по ширине',
          'ql-indent[value="-1"]': 'Отступ влево',
          'ql-indent[value="+1"]': 'Отступ вправо',
          'ql-link': 'Вставить ссылку',
          'ql-image': 'Вставить изображение',
          'ql-clean': 'Очистить форматирование',
          'ql-undo': 'Отменить',
          'ql-redo': 'Повторить',
          'ql-picker-label': 'Заголовки',
      }
    }
  },
  methods: {
    imageHandler() {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");

      input.addEventListener("change", async () => {
        const file = input.files[0];
        if (!file || this.uploading) return;

        try {
          this.uploading = true;
          const formData = new FormData();
          formData.append("attachment[file]", file);
          formData.append("attachment[kind]", "html_content_image");
          formData.append("attachment[type]", "Attachment::Document");

          const { data } = await this.$axios.post(
              this.$routes.attachments_path({ format: "json" }),
              formData
          );

          const quill = this.quill;
          const range = quill.getSelection();
          quill.insertEmbed(range.index, "image", data.resource.url);
        } catch (e) {
          console.error("Upload Error:", e);
        } finally {
          this.uploading = false;
        }
      });

      input.click();
    },
    updateUndoRedoButtons() {
      const canUndo = this.quill.history.stack.undo.length > 0;
      const canRedo = this.quill.history.stack.redo.length > 0;

      this.undoBtn.disabled = !canUndo;
      this.redoBtn.disabled = !canRedo;

      this.undoBtn.style.opacity = canUndo ? "1" : "0.5";
      this.redoBtn.style.opacity = canRedo ? "1" : "0.5";
    },
    setupTooltips() {
      const toolbar = this.$refs.editor.$el.querySelector('.ql-toolbar');
      if (toolbar) {
        Object.entries(this.tooltips).forEach(([selector, text]) => {
          const button = toolbar.querySelector(`.${selector}`);
          if (button) {
            button.setAttribute('aria-label', text);
            button.setAttribute('title', text);
          }
        });
      }
    }
  },
  mounted() {
    this.quill = new Quill(`#${this.editorId}`, {
      modules: {
        toolbar: {
          container: [
            ["undo", "redo"],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean'],
            ['image']
          ],
          handlers: {
            image: this.imageHandler,
            undo: () => this.quill.history.undo(),
            redo: () => this.quill.history.redo()
          }
        },
        resize: {
          locale: {
            floatLeft: "По левому",
            floatRight: "По правому",
            altTip: "Нажмите и удерживайте клавишу alt, чтобы зафиксировать соотношение!",
            inputTip: "Нажмите Enter для применения!",
            restore: "Сбросить",
            center: "По центру",
          },
        },
        history: {
          delay: 200,
          maxStack: 50,
          userOnly: true,
        },
      },
      theme: 'snow',
    });

    this.$nextTick(this.setupTooltips);

    this._options = Object.assign({}, this.options)
    this.content = this.value
    this.quill.setContents(this.quill.clipboard.convert({
      html: this.cmpValue
    }))
    this.labelFocus = !!this.focus || !!this.content

    this.undoBtn = this.$refs.editor.$el.querySelector(".ql-undo");
    this.redoBtn = this.$refs.editor.$el.querySelector(".ql-redo");

    this.updateUndoRedoButtons()

    this.quill.on('selection-change', range => {
      this.focus = !!range;
      this.labelFocus = !!this.focus || !!this.content
    })

    this.quill.on('text-change', () => {
      let html = this.$refs.editorField.children[0].innerHTML
      const quill = this.quill
      const text = this.quill.getText()
      if (html === '<p><br></p>') html = ''
      this.content = html
      this.cmpValue = this.content
      this.$emit('input', this.content)
      this.$emit('change', { html, text, quill })

      this.updateUndoRedoButtons()
    })

    // Emit ready event
    this.$emit('ready', this.quill)
  },
  beforeDestroy() {
    this.quill = null;
  },
}
</script>

<style lang="scss">
  @import '../../assets/styles/variables';
  $transition-dur: .2s;
  $header-values: (1, 2, 3, 4, 5, 6);

  #editor-resizer .toolbar {
    width: 28em !important;
  }

  .ql-snow {
    .ql-picker.ql-header {
      .ql-picker-label::before,
      .ql-picker-item::before {
        content: 'Обычный';
      }

      @each $value in $header-values {
        .ql-picker-label[data-value="#{$value}"]::before,
        .ql-picker-item[data-value="#{$value}"]::before {
          content: "Заголовок #{$value}";
        }
      }
    }

    .ql-picker:not(.ql-color-picker):not(.ql-icon-picker) svg {
      right: -4px;
    }

    .ql-picker-label {
      padding-left: 2px;
    }
  }

  .label-html-editor {
    position: absolute;
    font-size: 18px;
    left: 16px;
    top: 85px;
    color: rgb(135, 135, 135);
    transition: all $transition-dur;
    cursor: text;
    overflow-y: hidden;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(100% - 57px);
    pointer-events: none;

    &-focused {
      font-size: 14px;
      top: 66px;
    }
  }

  .html-editor {
    border: 1px solid whitesmoke;
    border-radius: 5px;
    padding: 26px 16px 16px 4px;
    background-color: whitesmoke;

    .hidden-textarea {
      display: none;
    }

    > .ql-container {
      border: none;

      > .ql-editor {
        font-size: 18px;
        line-height: 20px;
        padding: 0;
        margin: 26px 0 0 12px;
        caret-color: $color-caret;
        scrollbar-color: #888 whitesmoke;

        em {
          color: $color-text-black;
        }

        a {
          color: $color-text-link;
        }
      }
      > .ql-blank::before {
        font-style: normal;
        color: $color-placeholder;
        font-size: 18px;
      }
    }

    &-focusing {
      background: white;

      .ql-editor {
        &:focus-within {
          scrollbar-color: #888 white;
        }
      }
    }

    .ql-picker-options {
      background: whitesmoke;
    }

    .ql-snow .ql-tooltip {
      z-index: $html-editor-ql-tooltip-zindex;

      &::before{
        content: "Посещаемая ссылка:";
      }

      a.ql-remove::before {
        content: "Удалить";
      }

      a.ql-action::after {
        content: 'Редактировать';
      }
    }

    .ql-snow .ql-tooltip.ql-editing a.ql-action::after {
      content: 'Сохранить';
    }

    .ql-toolbar {
      padding: 0 0 10px 0;
      margin-left: 10px;
      border: none;
      border-bottom: 1px solid $color-more-dark-blue-20;

      > .ql-formats {
        margin-right: 12px;
      }

      &.ql-snow .ql-picker.ql-expanded .ql-picker-label {
        border: none;
      }

      .ql-undo {
        background-image: url("../../assets/images/html_editor/undo.svg");
      }

      .ql-redo {
        background-image: url('../../assets/images/html_editor/redo.svg');
      }

      .ql-undo, .ql-redo {
        &:disabled{
          pointer-events: none;
          opacity: 0.5;
        }

        &:hover {
          filter: invert(30%) sepia(100%) saturate(500%) hue-rotate(180deg);
        }
      }
    }
  }
</style>
